import React, { Component, useEffect } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import PropTypes from 'prop-types';

import Body from '../Components/layout/Body';
import { VISION_BUTTONS } from './ConstVisionBtns';

const VISUAL_ISSUES_HELP = `
  <div>
    <p><strong>Purpose: </strong>All of these issues can have a significant impact on access, and they may not be obvious or even known to the client.</p>

    <p>Here are a few indicators of vision issues, or things you might notice:
      <ul>
        <li><em>Blurred vision</em> - client squinting when attempting to look / read</li>
        <li><em>Cortical visual impairment</em> - characteristics vary, but may include some combination of: 
          <ul>
            <li>difficulty seeing an object in a busy background (difficulty with visual complexity)</li>
            <li>use of peripheral vision when reaching for an object (may exhibit a "look, look away, then reach" pattern when grasping or selecting)</li>
            <li>inconsistent response to visual cues</li>
            <li>longer visual processing times</li>
            <li>difficulty recognizing faces</li>
          </ul>
        <li><em>Double vision</em> - client closing one eye, or consistently off-shooting reach to target, may report seeing double.</li>
        <li><em>Field cut</em> - client is unable to locate a target, but then able to locate when target is moved to a different area.
        Might have difficulty with depth perception, bumping into things. Field cuts can be fairly complex, may not just be a right/left, could be in a single eye or both eyes, partial field cut(s), etc.  Sometimes field cuts are quadrant based.  Look up hemianopsia and note that there are several types!</li>
        <li><em>Tracking problems</em> - client's eyes don't move together (all the time? or only in certain parts of the visual field?)</li>
        <li><em>Other issues</em> can include complaints of eye pain / discomfort, difficulty fixating on a target, limited ability to move eyes, malalignment of eyes, and difficulty disassociating head movement from eye movement.</li>
      </ul>
    </p>
    <p>And a few other red flags to look out for:
      <ul>Does the person seem to look away when you present an item?  When they look away, is it always in the same direction?  They may be using peripheral vision or they may have a very small available visual field and they need to look away in order to see anything at all.</ul>
      <ul>Does the individual seem to have ‘inconsistent’ vision?  Sometimes they report seeing things you present, sometimes they can’t see anything?  Do they see things when just hanging out, but can’t when you ask them to look? This is a red flag.</ul>
      <ul>Does the client consistently close eyes when you ask them to do something?  Sometimes clients do this so they can reduce the amount of stimulation they are getting when listening to instructions, attempting to motor plan and execute a movement, etc.  </ul>
    </p>
    <p><strong>Note: </strong>Remember that if visual issues seem particularly complex with this individual, consider referral to a vision specialist.</p>
  </div>
`

const propTypes = {
  firstName: PropTypes.string,
  visionIssues: PropTypes.arrayOf(PropTypes.string),
  issuesNotes: PropTypes.string,
  onChangeVisionIssues: PropTypes.func.isRequired,
  onChangeIssuesNotes: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  visionIssues: [],
  issuesNotes: ''
};

//content sub-component with unique content for this page
class VisualIssuesContent extends Component {
  constructor(props){
    super(props);
  }

  //Render the form for vision issues.
  //Calls the setter that's passed in by the parent, when a checkbox is clicked or user types.
  //That setter changes the state of the vars in the parent, 
  //which triggers a new render of the view with the new var values.
  render(){
    const visionIssues = this.props.visionIssues ?? [];
    const issuesNotes = this.props.issuesNotes;
    const onChangeVisionIssues= this.props.onChangeVisionIssues;
    const onChangeIssuesNotes= this.props.onChangeIssuesNotes;

    let activeBtns = VISION_BUTTONS; 
    //Temporary fallback to make sure we always have some buttons

    return(
    <>
    <Form>
      <FormGroup tag="fieldset">
        <legend>Check all that apply:</legend>
        {activeBtns.map((btn, index) =>
          <FormGroup check key={index}>
            <Input
              id={btn.value}
              value={btn.value}
              checked={visionIssues.indexOf(btn.value) > -1}
              onChange={onChangeVisionIssues} 
              name="vision-issues"
              type="checkbox"
            />
            {' '}
            <Label for={btn.value} check>
              {btn.label}
              {' '}
              <span className="aa-more-info">{btn.moreInfo}</span>
            </Label>
          </FormGroup>
        )}
      </FormGroup>
      <FormGroup>
        <Label for="issues-notes" className="aa-bold-text">
          Notes on key vision issues:
        </Label>
        <Input
          id="issues-notes"
          value={issuesNotes}
          onChange={onChangeIssuesNotes}
          name="issues-notes"
          type="textarea"
        />
      </FormGroup>

    </Form>
    </>
  );
}
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component.
//since the main goal step includes a form, receive the initial form values via props,
//along with setters to change those values based on the form input.
function EvaluateAbilitiesVisualIssues(props) {
  const firstName = props.firstName;
  const visionIssues = props.visionIssues;
  const issuesNotes = props.issuesNotes;
  const onChangeVisionIssues= props.onChangeVisionIssues;
  const onChangeIssuesNotes= props.onChangeIssuesNotes;
  const saveFormData= props.saveFormData;
  const onContinue = props.onContinue;

  console.log("rendering EvaluateAbilitiesVisualIssues, issues: " + issuesNotes);

  const pageData = {
    image: "./assets/images/target-icon.jpg", //not yet used
    faIcon: "fa-eye",
    pageSymbolClassName: "aa-section-symbol eval",
    title: `Does ${firstName} appear to have any of these key vision issues?`,
    subTitle: `You can check for these informally by asking ${firstName} or through observation.`,
    learnMore: {linkText: "Learn More about key vision issues and what to look for",
                title: "Vision Issues",
                content: VISUAL_ISSUES_HELP
                },
    nextRoute: "", //not used since we have an onContinue callback
    sidebar: true
  }

  useEffect(() => {
    console.log('EAVBkgnd mounted');
    return () => {
      console.log('EAVBkgnd unmounting and saving...');
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <VisualIssuesContent 
          visionIssues={visionIssues} 
          onChangeVisionIssues={onChangeVisionIssues}
          issuesNotes={issuesNotes}
          onChangeIssuesNotes={onChangeIssuesNotes}
        />
      }
      onContinue={onContinue} 
    />
    </>
  );
}

EvaluateAbilitiesVisualIssues.propTypes = propTypes;
EvaluateAbilitiesVisualIssues.defaultProps = defaultProps;

export default EvaluateAbilitiesVisualIssues;